import React from 'react'
import { useState, useRef } from 'react'
import classNames from 'classnames'
import { t } from 'i18next'

import closeIcon from 'assets/images/icon-search-clear.svg'
import SearchIcon from 'assets/images/icon-toolbar-search.svg'
import BackIcon from 'assets/images/icon-arrow-left-gray.svg'

import styles from './style.module.scss'

interface SearchProps {
  value?: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onChangeBack?: () => void
  onClear?: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onPressEnter?: (value: string) => void
  prefix?: React.ReactNode
  placeholder?: string
  className?: string
}

const Search: React.FC<SearchProps> = ({
  value,
  onChange,
  onChangeBack,
  onClear,
  onKeyDown,
  onPressEnter,
  prefix,
  placeholder,
  className,
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const [isClear, setIsClear] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleClearClick = () => {
    if (onClear) {
      onClear()
    } else {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.value = ''
        }
      }, 100)
    }
    setIsClear(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onPressEnter) {
      onPressEnter(e.currentTarget.value)
    }
    if (onKeyDown) {
      onKeyDown(e)
    }
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if ((e?.relatedTarget as HTMLElement)?.classList?.contains(styles.iconButton)) {
      return
    }
  }

  const handleBackClick = () => {
    if (onClear) {
      onClear()
    }

    if (onChangeBack) {
      onChangeBack()
    }

    setIsFocused(false)
    setIsClear(false)
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.blur()
        inputRef.current.value = ''
      }
    }, 100)
  }

  const renderIcon = () => {
    if (prefix) {
      return prefix
    }

    if (isFocused) {
      return (
        <button
          type="button"
          className={styles.iconButton}
          onClick={handleBackClick}
          data-testid="search-back-button"
        >
          <img src={BackIcon} alt="Back" width="24" height="24" />
        </button>
      )
    }
    return <img src={SearchIcon} alt="Search" width="24" height="24" />
  }

  return (
    <div className={classNames(styles.search, className)} data-testid="search-section">
      <div className={styles.searchInnerWrapper}>
        {renderIcon()}
        <input
          ref={inputRef}
          type="text"
          className={styles.searchInput}
          placeholder={placeholder || t('mailbox.moveTab.search')}
          data-testid="search-input"
          value={value}
          onChange={(e) => {
            onChange(e)
            if (e?.target?.value?.trim() === '') {
              setIsClear(false)
            } else {
              setIsClear(true)
            }
          }}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {isClear ? (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClearClick}
            data-testid="search-clear-button"
          >
            <img src={closeIcon} alt="Clear search" width="16" height="16" />
          </button>
        ) : null}
      </div>
    </div>
  )
}

export default Search
